/**
 * Cloud projects.
 *
 * The blob is a packed `.rbxcloth` archive — one object per save, never one per
 * layer. Per-layer writes would multiply R2 Class A operations by layer count,
 * and the users with the most layers are exactly the ones paying us.
 */
import type { GarmentType } from "../../shared/format";
import { unpackProject } from "../../shared/pack";
import type { Tier } from "../../shared/tiers";
import { ApiError, rawRequest, request } from "./client";

export interface CloudProject {
  id: string;
  user_id: string;
  title: string;
  garment_type: GarmentType;
  current_version_id: string | null;
  revision: number;
  thumb_key: string | null;
  created_at: number;
  updated_at: number;
  /** 1-based position by recency. */
  rank: number;
  /**
   * Past the plan's project quota, so it accepts no writes.
   *
   * Set by the server after a downgrade. Nothing is ever deleted — the project
   * is still listed, openable and exportable; it just cannot be saved to until
   * the user resubscribes or deletes down to their quota.
   */
  readOnly: boolean;
}

export interface VersionSummary {
  id: string;
  revision: number;
  size_bytes: number;
  layer_count: number;
  created_at: number;
  created_by: string | null;
}

export async function listProjects(
  signal?: AbortSignal,
): Promise<{ projects: CloudProject[]; tier: Tier }> {
  return request("/api/projects", { signal });
}

export async function createProject(
  title: string,
  garmentType: GarmentType = "shirt",
): Promise<CloudProject> {
  const { project } = await request<{ project: CloudProject }>("/api/projects", {
    method: "POST",
    body: { title, garmentType },
  });
  return project;
}

export async function getProject(
  id: string,
  signal?: AbortSignal,
): Promise<{ project: CloudProject; versions: VersionSummary[] }> {
  return request(`/api/projects/${encodeURIComponent(id)}`, { signal });
}

/** Soft delete. Recoverable for 30 days, then the cron purges it for real. */
export async function deleteProject(id: string): Promise<void> {
  await request(`/api/projects/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export interface SaveResult {
  versionId: string;
  revision: number;
  sizeBytes: number;
  layerCount: number;
  pruned: number;
}

/** Exhaustive at compile time; asserted against the live response. */
export const SAVE_RESULT_KEYS = Object.keys({
  versionId: true,
  revision: true,
  sizeBytes: true,
  layerCount: true,
  pruned: true,
} satisfies Record<keyof SaveResult, true>);

/**
 * Save a new immutable version.
 *
 * `baseRevision` is the revision this edit was based on. If the stored revision
 * has moved on — another device, another tab — the server refuses with 409
 * rather than clobbering, and the caller has to decide what to do. **Do not
 * retry a 409 by bumping the revision**; that is the same as not having the
 * check at all.
 */
export async function saveVersion(
  id: string,
  bytes: Uint8Array,
  baseRevision: number,
  device: string,
  signal?: AbortSignal,
): Promise<SaveResult> {
  return request(`/api/projects/${encodeURIComponent(id)}/version`, {
    method: "PUT",
    bytes,
    headers: { "X-Base-Revision": String(baseRevision), "X-Device": device },
    signal,
    // Longer than the default: this uploads the whole archive, and a slow
    // connection on a large project is not a stall.
    timeoutMs: 60_000,
  });
}

/** Download a packed archive. Omit `versionId` for the current one. */
export async function loadBlob(
  id: string,
  versionId?: string,
  signal?: AbortSignal,
): Promise<{ bytes: Uint8Array; versionId: string }> {
  const query = versionId ? `?version=${encodeURIComponent(versionId)}` : "";
  const res = await rawRequest(`/api/projects/${encodeURIComponent(id)}/blob${query}`, {
    signal,
    timeoutMs: 60_000,
  });
  return {
    bytes: new Uint8Array(await res.arrayBuffer()),
    versionId: res.headers.get("X-Version-Id") ?? "",
  };
}

export interface LoadedProject {
  project: import("../../shared/format").Project;
  blobs: Record<string, Uint8Array>;
  thumb?: Uint8Array;
  versionId: string;
  /**
   * Layers an older archive lost on the way in, described for a human.
   *
   * Non-empty only for v1 archives, whose `fill`, `pattern`, `text` and
   * `watermark` kinds were generators no build ever rendered. Carried up rather
   * than logged because the only honest place to say "four layers from this
   * project no longer exist" is in front of the person who made them — and they
   * will notice the absence whether or not we mention it.
   */
  droppedOnMigration: string[];
}

/**
 * Fetch and unpack a project in one step.
 *
 * `loadBlob` returns bytes and nothing turned them into a `Project`, so
 * migration losses had nowhere to surface. Callers should prefer this.
 */
export async function loadProject(
  id: string,
  versionId?: string,
  signal?: AbortSignal,
): Promise<LoadedProject> {
  const { bytes, versionId: loaded } = await loadBlob(id, versionId, signal);
  const unpacked = unpackProject(bytes);
  return { ...unpacked, versionId: loaded };
}

/**
 * Why a save was refused, in words a person can act on.
 *
 * Three different 402s mean three different things, and "Payment required" is
 * the right answer to none of them. The server sends `code` precisely so this
 * can be a lookup rather than string-matching a message that will be reworded.
 */
export function explainSaveFailure(err: unknown): string {
  if (!(err instanceof ApiError)) return "Could not save. Your work is still here.";

  if (err.isOffline) return "You are offline. Your work is saved on this device.";

  switch (err.code) {
    case "project_read_only":
      return "This project is read-only on your current plan. Resubscribe, or delete a project to unlock it.";
    case "storage_exceeded":
      return "You have run out of storage on this plan.";
    case "quota_exceeded":
      return "You have reached the project limit for your plan.";
    case "upgrade_required":
      return "Cloud save needs a paid plan.";
    case "revision_conflict":
      return "This project was edited somewhere else. Reload it before saving again.";
    case "bad_archive":
      return "That file could not be read. Nothing was overwritten.";
    case "format_too_old":
      return "This project was made by an older version of the editor. Reload the page and open it again.";
    default:
      break;
  }

  if (err.isRateLimited) {
    const wait = err.retryAfter ? ` Try again in ${err.retryAfter}s.` : "";
    return `Saving too quickly.${wait}`;
  }
  if (err.isUnauthenticated) return "You are signed out. Sign in to save to the cloud.";
  return "Could not save. Your work is still here.";
}
